import React, { useEffect, useState } from 'react';
import Modal from '../common/Modal';
import { fieldStyles } from '../../config/formStyles';
import { theme } from '../../config/theme';
import { useColocadores } from '../../hooks/useColocadores';
import {
  COLOCADOR_DUPLICADO,
  createColocador,
  renameColocador,
  setColocadorActivo,
} from '../../services/colocadoresService';
import { formatCuitForDisplay, parseCuitDigits } from '../../utils/formatters';
import { describeCuitError } from '../../utils/cuit';

const styles = {
  intro: { margin: '0 0 1rem 0', fontSize: '0.875rem', color: theme.colors.textSecondary, lineHeight: 1.5 },
  sectionTitle: {
    fontFamily: theme.typography.titleFamily, fontSize: '0.8rem', fontWeight: '600',
    textTransform: 'uppercase', letterSpacing: '0.05em', color: theme.colors.primary, margin: '1.25rem 0 0.5rem 0',
  },
  form: { display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '0.75rem', alignItems: 'start' },
  fieldError: { fontSize: '0.75rem', color: theme.colors.errorText, marginTop: '0.25rem', lineHeight: 1.35 },
  list: {
    border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius,
    backgroundColor: theme.colors.surface, maxHeight: '40vh', overflowY: 'auto',
  },
  row: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
    padding: '0.7rem 1rem', borderBottom: `1px solid ${theme.colors.border}`, flexWrap: 'wrap',
  },
  rowMain: { display: 'flex', flexDirection: 'column', gap: '0.2rem', minWidth: 0 },
  rowName: { fontSize: '0.95rem', fontWeight: '600', color: theme.colors.secondary },
  rowMeta: { fontSize: '0.78rem', color: theme.colors.textSecondary },
  rowActions: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  badge: (active) => ({
    display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: '999px', fontSize: '0.7rem',
    fontWeight: '700', whiteSpace: 'nowrap',
    backgroundColor: active ? '#D1FAE5' : '#FEE2E2', color: active ? '#065F46' : '#991B1B',
  }),
  smallButton: { ...fieldStyles.button, padding: '0.35rem 0.75rem', fontSize: '0.78rem' },
  empty: { padding: '1.25rem 1rem', textAlign: 'center', fontSize: '0.875rem', color: theme.colors.textSecondary },
  feedback: (isError) => ({
    marginTop: '1rem', padding: '0.75rem 1rem', borderRadius: theme.borderRadius, fontSize: '0.85rem',
    backgroundColor: isError ? '#FEE2E2' : '#D1FAE5', color: isError ? '#991B1B' : '#065F46',
  }),
};

const FORM_VACIO = { nombre: '', cuit: '' };

const mensajeDeError = (error) => {
  if (error?.code === 'permission-denied') return 'Tu rol no puede modificar el catálogo de colocadores.';
  return error?.message && error.code === 'invalid-argument'
    ? error.message
    : 'No se pudo guardar el cambio. Intentá de nuevo.';
};

/**
 * Catálogo de colocadores de licitaciones (middle office y admin). Alta con nombre y
 * CUIT, cambio de nombre, desactivar y reactivar. No se borra nada: las licitaciones
 * ya cargadas guardan su propia copia de nombre y CUIT.
 */
const ColocadoresAdminModal = ({ isOpen, onClose }) => {
  const { colocadores, isLoading, error: errorLectura } = useColocadores({ enabled: isOpen });
  const [form, setForm] = useState(FORM_VACIO);
  const [editandoId, setEditandoId] = useState(null);
  const [intentoGuardar, setIntentoGuardar] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState({ message: '', isError: false });

  useEffect(() => {
    if (isOpen) return;
    setForm(FORM_VACIO);
    setEditandoId(null);
    setIntentoGuardar(false);
    setFeedback({ message: '', isError: false });
  }, [isOpen]);

  const cuitDigits = parseCuitDigits(form.cuit);
  const existente = editandoId ? null : colocadores.find(c => c.id === cuitDigits || c.cuit === cuitDigits);
  const errorNombre = form.nombre.trim() ? '' : 'Ingresá el nombre del colocador.';
  let errorCuit = editandoId ? '' : describeCuitError(form.cuit);
  if (!errorCuit && existente) {
    errorCuit = existente.active === false
      ? `Ya existe "${existente.nombre}" con ese CUIT, desactivado: reactivalo desde la lista.`
      : `Ya existe "${existente.nombre}" con ese CUIT.`;
  }

  const limpiarFormulario = () => {
    setForm(FORM_VACIO);
    setEditandoId(null);
    setIntentoGuardar(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIntentoGuardar(true);
    if (errorNombre || errorCuit) return;
    setIsSaving(true);
    setFeedback({ message: '', isError: false });
    try {
      if (editandoId) {
        await renameColocador(editandoId, form.nombre);
        setFeedback({ message: 'Nombre actualizado.', isError: false });
      } else {
        await createColocador({ nombre: form.nombre, cuit: cuitDigits });
        setFeedback({ message: `"${form.nombre.trim()}" agregado al catálogo.`, isError: false });
      }
      limpiarFormulario();
    } catch (error) {
      setFeedback({
        message: error.code === COLOCADOR_DUPLICADO ? error.message : mensajeDeError(error),
        isError: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditar = (colocador) => {
    setEditandoId(colocador.id);
    setForm({ nombre: colocador.nombre || '', cuit: formatCuitForDisplay(colocador.cuit) });
    setIntentoGuardar(false);
    setFeedback({ message: '', isError: false });
  };

  const handleToggleActivo = async (colocador) => {
    const desactivar = colocador.active !== false;
    if (desactivar && !window.confirm(
      `¿Desactivar a "${colocador.nombre}"? Deja de aparecer al cargar licitaciones nuevas; las ya cargadas no cambian.`,
    )) return;
    try {
      await setColocadorActivo(colocador.id, !desactivar);
      setFeedback({ message: `"${colocador.nombre}" ${desactivar ? 'desactivado' : 'reactivado'}.`, isError: false });
    } catch (error) {
      setFeedback({ message: mensajeDeError(error), isError: true });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Colocadores"
      maxWidth="760px"
      footer={
        <button type="button" onClick={onClose} style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton }}>Cerrar</button>
      }
    >
      <p style={styles.intro}>
        Entidades por las que se colocan las órdenes de las licitaciones. Al cargar una licitación nueva
        se eligen uno o más colocadores y el fee de colocación que paga cada uno. Si Axon Capital es
        colocador, el fee lo paga la emisora y se pide su CUIT.
      </p>

      <div style={{ ...styles.sectionTitle, marginTop: 0 }}>{editandoId ? 'Cambiar nombre' : 'Agregar colocador'}</div>
      <form onSubmit={handleSubmit} style={styles.form} className="axon-form-grid" noValidate>
        <div>
          <label style={fieldStyles.label} htmlFor="colocador-nombre">Nombre</label>
          <input
            id="colocador-nombre" type="text" value={form.nombre}
            onChange={(e) => setForm(prev => ({ ...prev, nombre: e.target.value }))}
            style={fieldStyles.input} placeholder="Ej: Banco de Servicios y Transacciones S.A.U."
            aria-invalid={intentoGuardar && !!errorNombre}
          />
          {intentoGuardar && errorNombre && <div style={styles.fieldError}>{errorNombre}</div>}
        </div>
        <div>
          <label style={fieldStyles.label} htmlFor="colocador-cuit">CUIT</label>
          <input
            id="colocador-cuit" type="text" inputMode="numeric" value={form.cuit}
            onChange={(e) => setForm(prev => ({ ...prev, cuit: e.target.value }))}
            onBlur={(e) => setForm(prev => ({ ...prev, cuit: formatCuitForDisplay(e.target.value) }))}
            style={editandoId ? { ...fieldStyles.input, backgroundColor: theme.colors.border } : fieldStyles.input}
            placeholder="Ej: 30-70496099-5" disabled={!!editandoId}
            title={editandoId ? 'El CUIT no se cambia: si está mal, desactivá el colocador y cargalo de nuevo.' : undefined}
            aria-invalid={(intentoGuardar || !!existente) && !!errorCuit}
          />
          {(intentoGuardar || !!existente) && errorCuit && <div style={styles.fieldError}>{errorCuit}</div>}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', paddingTop: '1.55rem' }}>
          <button type="submit" style={{ ...fieldStyles.button, padding: '0.5rem 1rem' }} disabled={isSaving}>
            {isSaving ? 'Guardando...' : (editandoId ? 'Guardar' : 'Agregar')}
          </button>
          {editandoId && (
            <button
              type="button" onClick={limpiarFormulario}
              style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton, padding: '0.5rem 1rem' }}
            >
              Cancelar
            </button>
          )}
        </div>
      </form>

      {feedback.message && <div style={styles.feedback(feedback.isError)} role="status">{feedback.message}</div>}

      <div style={styles.sectionTitle}>Catálogo</div>
      <div style={styles.list}>
        {isLoading && <div style={styles.empty}>Cargando colocadores...</div>}
        {!isLoading && errorLectura && (
          <div style={styles.empty}>No se pudo leer el catálogo. Revisá tu conexión e intentá de nuevo.</div>
        )}
        {!isLoading && !errorLectura && colocadores.length === 0 && (
          <div style={styles.empty}>Todavía no hay colocadores cargados.</div>
        )}
        {!isLoading && colocadores.map(colocador => {
          const activo = colocador.active !== false;
          return (
            <div key={colocador.id} style={{ ...styles.row, opacity: activo ? 1 : 0.65 }}>
              <div style={styles.rowMain}>
                <span style={styles.rowName}>{colocador.nombre}</span>
                <span style={styles.rowMeta}>CUIT {formatCuitForDisplay(colocador.cuit)}</span>
              </div>
              <div style={styles.rowActions}>
                <span style={styles.badge(activo)}>{activo ? 'Activo' : 'Inactivo'}</span>
                <button
                  type="button" onClick={() => handleEditar(colocador)}
                  style={{ ...styles.smallButton, ...fieldStyles.secondaryButton }}
                >
                  Cambiar nombre
                </button>
                <button
                  type="button" onClick={() => handleToggleActivo(colocador)}
                  style={activo ? { ...styles.smallButton, backgroundColor: '#B83227' } : styles.smallButton}
                >
                  {activo ? 'Desactivar' : 'Reactivar'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default ColocadoresAdminModal;
